import React, { useContext, useState, useEffect, useRef } from "react";
import classnames from "classnames";
import { BounceLoader } from "react-spinners";
import { TableWithPaginator, WidgetHead } from "components/common/DisplayElems";
import { Error } from "components/common/ControlElems";
import { TextField, Select } from "components/common/Form";
import { ContentContext } from "context/ContentContext";
import {
    DEFAULT_OFFER_DURATION_DAYS,
    DEFAULT_BATCH_SIZE,
    OFFER_DURATION_DAYS_MIN,
    OFFER_DURATION_DAYS_MAX,
    BATCH_SIZE_MIN,
    BATCH_SIZE_MAX,
    isReturningMemberCampaignActive,
    formatCampaignStatusLabel,
    getApiErrorMessage
} from "context/helpers/contentContextHelper";
import SendOfferDialog from "./SendOfferDialog";
import StartCampaignDialog from "./StartCampaignDialog";
import CancelCampaignDialog from "./CancelCampaignDialog";
import styles from "./ReturningMembers.module.scss";

const DEFAULT_FILTERS = {
    offerStatus: "all",
    id: "",
    email: "",
    login: "",
    offset: 0,
    limit: 20
};
const OFFER_STATUS_OPTIONS = [
    { value: "all", text: "All" },
    { value: "sent", text: "Sent" },
    { value: "not_sent", text: "Not sent" },
    { value: "active", text: "Active offer" }
];
const CAMPAIGN_POLL_MS = 10000;

export default function ReturningMembers({ history: routeHistory }) {
    const {
        returningMembersResult,
        returningMembersProcessing,
        returningMembersError,
        getReturningMembersList,
        returningMemberSettingsResult,
        returningMemberSettingsProcessing,
        returningMemberSettingsError,
        getReturningMemberSettings,
        saveReturningMemberSettings,
        saveReturningMemberSettingsProcessing,
        saveReturningMemberSettingsResult,
        saveReturningMemberSettingsError,
        sendReturningMemberOffer,
        sendReturningMemberOfferProcessing,
        sendReturningMemberOfferResult,
        sendReturningMemberOfferError,
        clearSendReturningMemberOffer,
        startReturningMemberCampaign,
        startReturningMemberCampaignProcessing,
        startReturningMemberCampaignError,
        clearStartReturningMemberCampaign,
        getReturningMemberCampaignStatus,
        returningMemberCampaignStatusResult,
        returningMemberCampaignStatusProcessing,
        returningMemberCampaignStatusError,
        cancelReturningMemberCampaign,
        cancelReturningMemberCampaignProcessing,
        cancelReturningMemberCampaignError,
        clearCancelReturningMemberCampaign
    } = useContext(ContentContext);
    const [fields, setFields] = useState(DEFAULT_FILTERS);
    const [offerDurationDays, setOfferDurationDays] = useState(
        DEFAULT_OFFER_DURATION_DAYS
    );
    const [batchSize, setBatchSize] = useState(DEFAULT_BATCH_SIZE);
    const [settingsValidationError, setSettingsValidationError] = useState("");
    const [sendTarget, setSendTarget] = useState(null);
    const [confirmCampaign, setConfirmCampaign] = useState(false);
    const [confirmCancelCampaign, setConfirmCancelCampaign] = useState(false);
    const [pollCampaign, setPollCampaign] = useState(false);
    const campaignActive = isReturningMemberCampaignActive(
        returningMemberCampaignStatusResult
    );
    const wasCampaignActive = useRef(false);

    useEffect(() => {
        getReturningMembersList(fields);
    }, [fields.limit, fields.offset, fields.offerStatus]);

    useEffect(() => {
        getReturningMemberSettings();
        getReturningMemberCampaignStatus();
    }, []);

    useEffect(() => {
        if (campaignActive) {
            setPollCampaign(true);
        }
    }, [campaignActive]);

    useEffect(() => {
        if (
            pollCampaign &&
            returningMemberCampaignStatusResult &&
            !campaignActive
        ) {
            setPollCampaign(false);
        }
    }, [pollCampaign, campaignActive, returningMemberCampaignStatusResult]);

    useEffect(() => {
        if (!pollCampaign) return undefined;
        const intervalId = setInterval(() => {
            getReturningMemberCampaignStatus();
        }, CAMPAIGN_POLL_MS);
        return () => clearInterval(intervalId);
    }, [pollCampaign]);

    useEffect(() => {
        if (wasCampaignActive.current && !campaignActive) {
            getReturningMembersList(fields);
        }
        wasCampaignActive.current = campaignActive;
    }, [campaignActive]);

    useEffect(() => {
        if (!returningMemberSettingsResult) return;
        if (returningMemberSettingsResult.offerDurationDays != null) {
            setOfferDurationDays(
                returningMemberSettingsResult.offerDurationDays
            );
        }
        if (returningMemberSettingsResult.batchSize != null) {
            setBatchSize(returningMemberSettingsResult.batchSize);
        }
    }, [returningMemberSettingsResult]);

    const listData = returningMembersResult || { list: [], count: 0 };
    const pageLimit = Number.parseInt(fields.limit, 10) || 20;
    const pageOffset = Number.parseInt(fields.offset, 10) || 0;
    const pageList = listData.list || [];
    const apiCount = Number.parseInt(listData.count, 10) || 0;
    const paginationCount =
        !returningMembersProcessing &&
        pageOffset === 0 &&
        pageList.length < pageLimit
            ? pageList.length
            : apiCount;

    const handleSaveSettings = () => {
        if (saveReturningMemberSettingsProcessing) return;
        const daysError = getOfferDurationError(offerDurationDays);
        if (daysError) {
            setSettingsValidationError(daysError);
            return;
        }
        const batchError = getBatchSizeError(batchSize);
        if (batchError) {
            setSettingsValidationError(batchError);
            return;
        }
        setSettingsValidationError("");
        saveReturningMemberSettings({
            offerDurationDays: Number(String(offerDurationDays).trim()),
            batchSize: Number(String(batchSize).trim())
        });
    };

    const handleResetFilters = () => {
        if (returningMembersProcessing) return;
        const resetFields = {
            ...DEFAULT_FILTERS,
            limit: fields.limit
        };
        setFields(resetFields);
        getReturningMembersList(resetFields);
    };

    const filtersAreDefault =
        fields.offerStatus === DEFAULT_FILTERS.offerStatus &&
        !fields.id &&
        !fields.email &&
        !fields.login &&
        Number(fields.offset) === 0;

    const handleOpenSend = member => {
        clearSendReturningMemberOffer();
        setSendTarget(member);
    };

    const handleCloseSend = () => {
        setSendTarget(null);
        clearSendReturningMemberOffer();
    };

    const handleSendOffer = async payload => {
        try {
            await sendReturningMemberOffer(payload);
            getReturningMembersList(fields);
        } catch (_) {
            // Error is stored in context; dialog displays it.
        }
    };

    const handleOpenCampaignConfirm = () => {
        if (startReturningMemberCampaignProcessing || campaignActive) return;
        clearStartReturningMemberCampaign();
        setConfirmCampaign(true);
    };

    const handleCloseCampaignConfirm = () => {
        if (startReturningMemberCampaignProcessing) return;
        setConfirmCampaign(false);
    };

    const handleStartCampaign = async campaignOptions => {
        try {
            await startReturningMemberCampaign(campaignOptions || {});
            setConfirmCampaign(false);
            setPollCampaign(true);
            await getReturningMemberCampaignStatus();
        } catch (error) {
            if (error && error.code === 409) {
                setPollCampaign(true);
                await getReturningMemberCampaignStatus();
            }
        }
    };

    const handleOpenCancelCampaign = () => {
        if (!campaignActive || cancelReturningMemberCampaignProcessing) return;
        clearCancelReturningMemberCampaign();
        setConfirmCancelCampaign(true);
    };

    const handleCloseCancelCampaign = () => {
        if (cancelReturningMemberCampaignProcessing) return;
        setConfirmCancelCampaign(false);
    };

    const handleCancelCampaign = async () => {
        try {
            await cancelReturningMemberCampaign();
            setConfirmCancelCampaign(false);
            setPollCampaign(true);
            await getReturningMemberCampaignStatus();
        } catch (_) {
            // Error is stored in context; dialog displays it.
        }
    };

    const campaignStatus = returningMemberCampaignStatusResult || {};

    return (
        <div className="col-xl-12">
            <div className="m-portlet">
                <WidgetHead label="Returning Members" />
                {returningMembersError && (
                    <Error
                        text={
                            returningMembersError.message ||
                            "Failed to load returning members"
                        }
                    />
                )}

                <WidgetHead
                    label="Offer Settings"
                    button={{
                        label: saveReturningMemberSettingsProcessing
                            ? "Saving..."
                            : "Save",
                        onClick: handleSaveSettings,
                        disabled:
                            saveReturningMemberSettingsProcessing ||
                            returningMemberSettingsProcessing
                    }}
                />
                <div className="d-flex flex-row justify-content-sm-start pl-xl-4 pt-xl-4 pb-xl-3">
                    <div className={classnames(styles["left-panel"])}>
                        <TextField
                            value={offerDurationDays}
                            name="offerDurationDays"
                            label="Offer duration (days)"
                            type="number"
                            step="1"
                            min={OFFER_DURATION_DAYS_MIN}
                            max={OFFER_DURATION_DAYS_MAX}
                            placeholder={String(DEFAULT_OFFER_DURATION_DAYS)}
                            disabled={
                                returningMemberSettingsProcessing ||
                                saveReturningMemberSettingsProcessing
                            }
                            onKeyDown={preventNonIntegerKeys}
                            onChange={event => {
                                const value = event.target.value;
                                setOfferDurationDays(value);
                                setSettingsValidationError(
                                    value === ""
                                        ? ""
                                        : getOfferDurationError(value)
                                );
                            }}
                        />
                        <TextField
                            value={batchSize}
                            name="batchSize"
                            label="Campaign batch size (emails per run)"
                            type="number"
                            step="1"
                            min={BATCH_SIZE_MIN}
                            max={BATCH_SIZE_MAX}
                            placeholder={String(DEFAULT_BATCH_SIZE)}
                            disabled={
                                returningMemberSettingsProcessing ||
                                saveReturningMemberSettingsProcessing
                            }
                            onKeyDown={preventNonIntegerKeys}
                            onChange={event => {
                                const value = event.target.value;
                                setBatchSize(value);
                                setSettingsValidationError(
                                    value === ""
                                        ? ""
                                        : getBatchSizeError(value)
                                );
                            }}
                        />
                        {settingsValidationError && (
                            <Error text={settingsValidationError} />
                        )}
                        {returningMemberSettingsError && (
                            <Error
                                text={
                                    returningMemberSettingsError.message ||
                                    "Failed to load offer settings"
                                }
                            />
                        )}
                        {saveReturningMemberSettingsError && (
                            <Error
                                text={
                                    saveReturningMemberSettingsError.message ||
                                    "Failed to save offer settings"
                                }
                            />
                        )}
                        {saveReturningMemberSettingsResult && (
                            <p className={styles["settings-success"]}>
                                Offer settings saved.
                            </p>
                        )}
                    </div>
                </div>

                <WidgetHead
                    label="Campaign"
                    button={{
                        label: startReturningMemberCampaignProcessing
                            ? "Creating..."
                            : campaignActive
                            ? "Campaign running..."
                            : "Create Campaign",
                        onClick: handleOpenCampaignConfirm,
                        disabled:
                            startReturningMemberCampaignProcessing ||
                            campaignActive
                    }}
                />
                <div className="px-4 pt-xl-4 pb-xl-3">
                    <div>
                        {campaignActive && (
                            <div className={styles["campaign-toolbar"]}>
                                <button
                                    type="button"
                                    className="btn btn-danger"
                                    onClick={handleOpenCancelCampaign}
                                    disabled={
                                        cancelReturningMemberCampaignProcessing
                                    }
                                >
                                    {cancelReturningMemberCampaignProcessing
                                        ? "Cancelling..."
                                        : "Cancel Campaign"}
                                </button>
                            </div>
                        )}
                        <div className={styles["campaign-status"]}>
                            <div className={styles["campaign-stat"]}>
                                <span className={styles["campaign-stat-label"]}>
                                    Status
                                </span>
                                <span className={styles["campaign-stat-value"]}>
                                    {formatCampaignStatusLabel(
                                        campaignStatus.status
                                    )}
                                </span>
                            </div>
                            <div className={styles["campaign-stat"]}>
                                <span className={styles["campaign-stat-label"]}>
                                    Total queued
                                </span>
                                <span className={styles["campaign-stat-value"]}>
                                    {campaignStatus.totalQueued != null
                                        ? campaignStatus.totalQueued
                                        : "-"}
                                </span>
                            </div>
                            <div className={styles["campaign-stat"]}>
                                <span className={styles["campaign-stat-label"]}>
                                    Total sent
                                </span>
                                <span className={styles["campaign-stat-value"]}>
                                    {campaignStatus.totalSent != null
                                        ? campaignStatus.totalSent
                                        : "-"}
                                </span>
                            </div>
                            <div className={styles["campaign-stat"]}>
                                <span className={styles["campaign-stat-label"]}>
                                    Total failed
                                </span>
                                <span className={styles["campaign-stat-value"]}>
                                    {campaignStatus.totalFailed != null
                                        ? campaignStatus.totalFailed
                                        : "-"}
                                </span>
                            </div>
                            <div className={styles["campaign-stat"]}>
                                <span className={styles["campaign-stat-label"]}>
                                    Total skipped
                                </span>
                                <span className={styles["campaign-stat-value"]}>
                                    {campaignStatus.totalSkipped != null
                                        ? campaignStatus.totalSkipped
                                        : "-"}
                                </span>
                            </div>
                            <div className={styles["campaign-stat"]}>
                                <span className={styles["campaign-stat-label"]}>
                                    Total pending
                                </span>
                                <span className={styles["campaign-stat-value"]}>
                                    {campaignStatus.totalPending != null
                                        ? campaignStatus.totalPending
                                        : "-"}
                                </span>
                            </div>
                            {returningMemberCampaignStatusProcessing && (
                                <p className={styles["campaign-updating"]}>
                                    Updating campaign status...
                                </p>
                            )}
                        </div>
                        {returningMemberCampaignStatusError && (
                            <Error
                                text={
                                    returningMemberCampaignStatusError.message ||
                                    "Failed to load campaign status"
                                }
                            />
                        )}
                        {startReturningMemberCampaignError &&
                            !confirmCampaign && (
                                <Error
                                    text={getApiErrorMessage(
                                        startReturningMemberCampaignError,
                                        "Failed to start campaign"
                                    )}
                                />
                            )}
                        {cancelReturningMemberCampaignError &&
                            !confirmCancelCampaign && (
                                <Error
                                    text={getApiErrorMessage(
                                        cancelReturningMemberCampaignError,
                                        "Failed to cancel campaign"
                                    )}
                                />
                            )}
                    </div>
                </div>

                <WidgetHead
                    label="Filter"
                    extraButton={{
                        label: "Reset Filters",
                        className: "btn btn-secondary",
                        onClick: handleResetFilters,
                        disabled:
                            returningMembersProcessing || filtersAreDefault
                    }}
                    button={{
                        label: returningMembersProcessing
                            ? "Searching..."
                            : "Search",
                        onClick: () => {
                            if (returningMembersProcessing) return;
                            const nextFields = { ...fields, offset: 0 };
                            setFields(nextFields);
                            getReturningMembersList(nextFields);
                        },
                        disabled: returningMembersProcessing
                    }}
                />
                <div className="d-flex flex-row flex-wrap justify-content-sm-start pl-xl-4 pt-xl-4">
                    <div className={classnames(styles["left-panel"])}>
                        <Select
                            name="offerStatus"
                            label="Offer status"
                            className={styles["filter-select"]}
                            value={fields.offerStatus}
                            options={OFFER_STATUS_OPTIONS}
                            onChange={event =>
                                handleSetFields(
                                    "offerStatus",
                                    fields,
                                    event,
                                    setFields
                                )
                            }
                        />
                        <TextField
                            value={fields.id}
                            name="Id"
                            label="Id"
                            type="number"
                            min="0"
                            placeholder="Id"
                            onChange={event =>
                                handleSetFields("id", fields, event, setFields)
                            }
                        />
                    </div>
                    <div className={classnames(styles["left-panel"])}>
                        <TextField
                            value={fields.email}
                            name="Email"
                            label="Email"
                            type="text"
                            placeholder="Email"
                            onChange={event =>
                                handleSetFields(
                                    "email",
                                    fields,
                                    event,
                                    setFields
                                )
                            }
                        />
                        <TextField
                            value={fields.login}
                            name="Login"
                            label="Login"
                            type="text"
                            placeholder="Login"
                            onChange={event =>
                                handleSetFields(
                                    "login",
                                    fields,
                                    event,
                                    setFields
                                )
                            }
                        />
                    </div>
                </div>
                <div className={styles["table-wrap"]}>
                    {returningMembersProcessing && (
                        <div className={styles["table-loader"]}>
                            <BounceLoader
                                loading={true}
                                size={48}
                                color="#34bfa3"
                            />
                            <span className="loading-text">Loading...</span>
                        </div>
                    )}
                    <TableWithPaginator
                    buttons={[
                        {
                            label: "Info",
                            action: elem =>
                                routeHistory.push("/djuserdetails/" + elem.id),
                            className: "btn-primary w-60"
                        },
                        {
                            label: "Send Offer",
                            action: handleOpenSend,
                            className: "btn-success w-60"
                        }
                    ]}
                    tableElems={[
                        "id",
                        "email",
                        "fullName",
                        "login",
                        "lastExpiredDate",
                        "offerSent",
                        "hasActiveOffer",
                        renderLastOfferSent,
                        "lastSentToEmail"
                    ]}
                    tableLabel="Returning Members"
                    headers={[
                        "Id",
                        "Email",
                        "FullName",
                        "Login",
                        "Last Expired Date",
                        "Offer Sent",
                        "Active Offer",
                        "Last Offer Sent",
                        "Last Sent To",
                        "Actions"
                    ]}
                    data={pageList}
                    isDataFetching={returningMembersProcessing}
                    tableWidth={[
                        "80px",
                        "180px",
                        "160px",
                        "120px",
                        "140px",
                        "100px",
                        "110px",
                        "150px",
                        "180px",
                        "180px"
                    ]}
                    offset={pageOffset}
                    limit={pageLimit}
                    count={paginationCount}
                    options={[
                        { value: 5, text: "5" },
                        { value: 10, text: "10" },
                        { value: 20, text: "20" },
                        { value: 50, text: "50" }
                    ]}
                    offsetOnChange={value =>
                        handleSetFields(
                            "offset",
                            fields,
                            { target: { value } },
                            setFields
                        )
                    }
                    limitOnChange={event =>
                        handleSetFields("limit", fields, event, setFields)
                    }
                    />
                </div>
            </div>

            {sendTarget && (
                <SendOfferDialog
                    member={sendTarget}
                    processing={sendReturningMemberOfferProcessing}
                    result={sendReturningMemberOfferResult}
                    error={sendReturningMemberOfferError}
                    onClose={handleCloseSend}
                    onSend={handleSendOffer}
                />
            )}
            {confirmCampaign && (
                <StartCampaignDialog
                    processing={startReturningMemberCampaignProcessing}
                    error={startReturningMemberCampaignError}
                    defaultCount={batchSize}
                    availableCount={listData.count}
                    onClose={handleCloseCampaignConfirm}
                    onConfirm={handleStartCampaign}
                />
            )}
            {confirmCancelCampaign && (
                <CancelCampaignDialog
                    processing={cancelReturningMemberCampaignProcessing}
                    error={cancelReturningMemberCampaignError}
                    onClose={handleCloseCancelCampaign}
                    onConfirm={handleCancelCampaign}
                />
            )}
        </div>
    );
}

function renderLastOfferSent(member) {
    const value = member && member.lastOfferSentAt;
    if (!value || value === "-") return "-";
    const match = String(value)
        .trim()
        .match(/^(\d{4}-\d{2}-\d{2})[ T](\d{2}:\d{2})/);
    if (!match) return value;
    return (
        <span className={styles["last-offer-sent"]}>
            <span>{match[1]}</span>
            <span>{match[2]}</span>
        </span>
    );
}

function handleSetFields(key, fields, event, handler) {
    const newFields = { ...fields };
    const value = event.target.value;
    newFields[key] = value == null ? "" : value;
    if (key !== "offset") newFields.offset = 0;
    handler(newFields);
}

function isWholeNumberInput(value) {
    return /^\d+$/.test(String(value == null ? "" : value).trim());
}

function preventNonIntegerKeys(event) {
    if ([".", ",", "e", "E", "+", "-"].includes(event.key)) {
        event.preventDefault();
    }
}

function getOfferDurationError(value) {
    if (!isWholeNumberInput(value)) {
        return "Offer duration must be a whole number.";
    }
    const days = Number(String(value).trim());
    if (days < OFFER_DURATION_DAYS_MIN || days > OFFER_DURATION_DAYS_MAX) {
        return `Offer duration must be between ${OFFER_DURATION_DAYS_MIN} and ${OFFER_DURATION_DAYS_MAX} days.`;
    }
    return "";
}

function getBatchSizeError(value) {
    if (!isWholeNumberInput(value)) {
        return "Batch size must be a whole number.";
    }
    const batch = Number(String(value).trim());
    if (batch < BATCH_SIZE_MIN || batch > BATCH_SIZE_MAX) {
        return `Batch size must be between ${BATCH_SIZE_MIN} and ${BATCH_SIZE_MAX}.`;
    }
    return "";
}
